import { FC, useState, useRef, useEffect, KeyboardEvent } from "react"
import { useDispatch, useSelector } from "../../../hooks/redux"
import { updateSelectedUsersList } from "../../../store/slices/admin/adminSlice"
import { Checkbox } from "../../../components/checkbox/checkbox"
import { CustomButton } from "../../../components/customButton"
import { CustomButtonSelect } from "../../../components/customButtonSelect"
import { CustomIcon } from "../../../components/customIcon/customIcon"
import classes from '../../../styles/admin/usersList.module.scss'
import { IUser, PartialUser } from "../../../@types/user"
import { updateUserSettings } from "../../../store/slices/user/userSlice"
import { addNotify } from "../../../store/slices/notify/notifySlice"
import { setModalVisibility, updateModalSettings } from "../../../store/slices/admin/adminSlice"
import { UsersListItemMenu } from "./usersListItemMenu"
import cn from 'classnames'


export const UsersListItem: FC<{
    config: IUser,
    isSelected: boolean,
    index: number
}> = ({
    config,
    isSelected,
    index
}) => {
        const dispatch = useDispatch()
        const changedUsers = useSelector((state) => state.user.changes)
        const initialUser = useSelector((state) => state.user.initialUsers.find(u => u.user_id === config.user_id))
        const tokensVisible = useSelector((state) => state.admin.tokensVisible)

        const [tokenLocalVisible, setTokenLocalVisible] = useState(false)

        useEffect(() => {
            if (!tokensVisible) setTokenLocalVisible(false)
        }, [tokensVisible])
        const [tokenDraft, setTokenDraft] = useState('')
        const [nameDraft, setNameDraft] = useState('')
        const [nameEditing, setNameEditing] = useState(false)
        const [menuOpenTop, setMenuOpenTop] = useState(false)
        const moreBtnRef = useRef<HTMLDivElement>(null)
        const tokenInputRef = useRef<HTMLInputElement>(null)
        const nameInputRef = useRef<HTMLInputElement>(null)
        const tokenVisible = tokensVisible || tokenLocalVisible

        const handleNameClick = () => {
            setNameDraft(auth_username ?? '')
            setNameEditing(true)
            setTimeout(() => nameInputRef.current?.focus(), 0)
        }

        const handleNameBlur = () => {
            const trimmed = nameDraft.trim()
            if (trimmed && trimmed !== auth_username) {
                dispatch(updateUserSettings({ auth_username: trimmed, user_id: config.user_id }))
            }
            setNameEditing(false)
        }

        const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                nameInputRef.current?.blur()
            } else if (e.key === 'Escape') {
                setNameEditing(false)
            }
        }

        const handleTokenBlur = () => {
            const trimmed = tokenDraft.trim()
            if (trimmed && trimmed !== auth_token) {
                dispatch(updateUserSettings({ auth_token: trimmed, user_id: config.user_id }))
            }
        }

        const handleTokenKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                tokenInputRef.current?.blur()
            } else if (e.key === 'Escape') {
                setTokenDraft(auth_token ?? '')
                tokenInputRef.current?.blur()
            }
        }

        const isModified = (key: keyof IUser) => initialUser && initialUser[key] !== config[key]

        const handleChangeSettings = (permissions: PartialUser) => {
            dispatch(updateUserSettings({ ...permissions, user_id: config.user_id }))
        }

        const selectUser = () => {
            dispatch(updateSelectedUsersList(config.user_id))
        }

        const {
            auth_username,
            auth_token,
            access_asr,
            access_tts,
            access_console,
            access_admin
        } = config

        return (
            <div className={cn(classes.usersListItem, changedUsers.find(id => id === config.user_id) && classes.changed)}>
                <span className={classes.itemIndex}>
                    {changedUsers.find(id => id === config.user_id) && <span className={classes.changeMarker} />}
                    {index}
                </span>
                <Checkbox isChecked={isSelected} handleCheckboxChange={selectUser} />
                {nameEditing ? (
                    <input
                        ref={nameInputRef}
                        className={classes.itemNameInput}
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onBlur={handleNameBlur}
                        onKeyDown={handleNameKeyDown}
                    />
                ) : (
                    <span className={classes.itemName} onClick={handleNameClick}>{auth_username}</span>
                )}
                <span className={classes.tokenActions}>
                    <button className={classes.tokenToggle} onClick={() => setTokenLocalVisible(v => !v)}>
                        <CustomIcon icon={tokenVisible ? 'hideIcon' : 'showIcon'} />
                    </button>
                    <button className={classes.tokenToggle} onClick={() => { navigator.clipboard.writeText(auth_token ?? ''); dispatch(addNotify({ text: 'Токен скопирован' })) }}>
                        <CustomIcon icon="copy" />
                    </button>
                </span>
                <span className={classes.itemToken}>
                    {tokenVisible ? (
                        <input
                            ref={tokenInputRef}
                            className={classes.itemTokenInput}
                            value={tokenDraft || (auth_token ?? '')}
                            onChange={(e) => setTokenDraft(e.target.value.replace(/[^\x00-\x7F]/g, ''))}
                            onFocus={() => setTokenDraft(auth_token ?? '')}
                            onBlur={handleTokenBlur}
                            onKeyDown={handleTokenKeyDown}
                        />
                    ) : (
                        <span className={classes.itemTokenDots} />
                    )}
                </span>
                <div className={classes.itemAccesses}>
                    <CustomButton
                        onClick={() => handleChangeSettings({ access_asr: !access_asr })}
                        className={cn(classes.accessButton, access_asr ? classes.active : classes.disabled, isModified('access_asr') && classes.modified)}
                    >
                        ASR
                    </CustomButton>
                    <CustomButton
                        onClick={() => handleChangeSettings({ access_tts: !access_tts })}
                        className={cn(classes.accessButton, access_tts ? classes.active : classes.disabled, isModified('access_tts') && classes.modified)}
                    >
                        TTS
                    </CustomButton>
                    <CustomButton
                        onClick={() => handleChangeSettings({ access_console: !access_console })}
                        className={cn(classes.accessButton, access_console ? classes.active : classes.disabled, isModified('access_console') && classes.modified)}
                    >
                        Консоль
                    </CustomButton>
                    <CustomButton
                        onClick={() => handleChangeSettings({ access_admin: !access_admin })}
                        className={cn(classes.accessButton, access_admin ? classes.active : classes.disabled, isModified('access_admin') && classes.modified)}
                    >
                        Админка
                    </CustomButton>
                </div>
                <div className={classes.itemActions} ref={moreBtnRef}>
                    <button
                        className={classes.deleteUserBtn}
                        onClick={() => {
                            dispatch(setModalVisibility('delete'))
                            dispatch(updateModalSettings(config))
                        }}
                    >
                        <CustomIcon icon="delete" />
                        Удалить
                    </button>
                    <CustomButtonSelect
                        cloneChild
                        classes={{ button: classes.moreBtn, content: cn(classes.moreBtnContent, menuOpenTop && classes.moreBtnContentTop) }}
                        onClick={() => {
                            if (moreBtnRef.current) {
                                const rect = moreBtnRef.current.getBoundingClientRect()
                                setMenuOpenTop(window.innerHeight - rect.bottom < 280)
                            }
                        }}
                        contentComponent={
                            <UsersListItemMenu
                                config={config}
                                tokenVisible={tokenVisible}
                                onToggleToken={() => setTokenLocalVisible(v => !v)}
                            />
                        }
                    >
                        <CustomIcon icon="dotsVertical" />
                    </CustomButtonSelect>
                </div>
            </div>
        )
    }
